import React, { useState, useEffect, useRef, useMemo } from "react";
import ReactDOM from "react-dom";
import { Button, Icon } from "semantic-ui-react";
import { toast, ToastContainer } from "react-toastify";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import { LoadingSpinner, BasicDrawer, ConfirmDialog, FileDropzone, EvidenceTextButton } from "../../../Shared";
import { buildStandardDashboardPdf, drawPill } from "../../../Shared/pdfCharts";
import { WorkPermitForm }   from "../WorkPermitForm/WorkPermitForm";
import { WorkPermitDetail } from "../WorkPermitDetail/WorkPermitDetail";
import { ActionPlanForm }   from "../../ActionPlans/ActionPlanForm";
import { WorkPermit, Site, Company } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { useSite, useCompany, useLanguage } from "../../../../contexts";
import "./ListWorkPermits.scss";

const wpController     = new WorkPermit();
const siteCtrl         = new Site();
const companyCtrl      = new Company();

// ── Opciones de estado (para filtro) ────────────────────────────────────────
const STATUS_OPTIONS = [
  { key: "draft",        value: "draft"        },
  { key: "requested",    value: "requested"    },
  { key: "pending_n2",   value: "pending_n2"   },
  { key: "pending_n3",   value: "pending_n3"   },
  { key: "pending_n4",   value: "pending_n4"   },
  { key: "approved",     value: "approved"     },
  { key: "in_execution", value: "in_execution" },
  { key: "verified",     value: "verified"     },
  { key: "suspended",    value: "suspended"    },
  { key: "paused",       value: "paused"       },
  { key: "rejected",     value: "rejected"     },
  { key: "expired",      value: "expired"      },
  { key: "closed",       value: "closed"       },
];

// ── Metadatos por tipo de permiso ────────────────────────────────────────────
const TIPO_META = {
  altura:            { label: "Altura",            color: "#f59e0b" },
  caliente:          { label: "Caliente",           color: "#ef4444" },
  espacio_confinado: { label: "Espacio confinado",  color: "#14b8a6" },
  electrico_loto:    { label: "Eléctrico / LOTO",   color: "#8b5cf6" },
  izaje:             { label: "Izaje",              color: "#64748b" },
  quimicos:          { label: "Químicos",           color: "#16a34a" },
  excavacion:        { label: "Excavación",         color: "#d97706" },
  caldera:           { label: "Caldera",            color: "#3b82f6" },
  demolicion:        { label: "Demolición",         color: "#ec4899" },
  acceso_techo:      { label: "Acceso a techo",     color: "#06b6d4" },
  radiaciones:       { label: "Radiaciones",        color: "#7c3aed" },
};

// ── Metadatos visuales por estado ────────────────────────────────────────────
const STATUS_META = {
  draft:        { color: "#64748b", bg: "#f1f5f9" },
  requested:    { color: "#d97706", bg: "#fef9c3" },
  pending_n2:   { color: "#d97706", bg: "#fef3c7" },
  pending_n3:   { color: "#d97706", bg: "#fef3c7" },
  pending_n4:   { color: "#d97706", bg: "#fef3c7" },
  approved:     { color: "#16a34a", bg: "#dcfce7" },
  in_execution: { color: "#16a34a", bg: "#dcfce7", dot: true },
  verified:     { color: "#16a34a", bg: "#dcfce7" },
  suspended:    { color: "#ea580c", bg: "#ffedd5" },
  paused:       { color: "#f97316", bg: "#fff7ed" },
  rejected:     { color: "#dc2626", bg: "#fee2e2" },
  expired:      { color: "#dc2626", bg: "#fee2e2" },
  closed:       { color: "#475569", bg: "#e2e8f0" },
};

// ── Niveles aprobados según estado ──────────────────────────────────────────
const approvedLevels = (status) => {
  switch (status) {
    case "closed":                               return 5;
    case "approved": case "in_execution":
    case "verified": case "paused":              return 4;
    case "pending_n4":                           return 3;
    case "pending_n3": case "suspended":         return 2;
    case "pending_n2": case "rejected":          return 1;
    default:                                     return 0;
  }
};

// ── Nombres de países ────────────────────────────────────────────────────────
const PAIS_LABELS = {
  AR: "Argentina", BO: "Bolivia",  BR: "Brasil",   CL: "Chile",
  CO: "Colombia",  CR: "Costa Rica", DO: "Rep. Dominicana", EC: "Ecuador",
  GT: "Guatemala", MX: "México",   NI: "Nicaragua", PA: "Panamá",
  PE: "Perú",      PY: "Paraguay", SV: "El Salvador", UY: "Uruguay", VE: "Venezuela",
};

// ── Helpers ──────────────────────────────────────────────────────────────────
const fmtPermitNum = (p) => {
  if (!p.customId) return "-";
  const year = p.createdAt ? new Date(p.createdAt).getFullYear() : new Date().getFullYear();
  return `PT-${year}-${String(p.customId).padStart(5, "0")}`;
};

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric" }) : null;
const fmtTime = (d) =>
  d ? new Date(d).toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" }) : null;

const fmtEjecutorDisplay = (p) => {
  if (p.ejecutora === "contratista")     return p.contratista?.nombre || "Contratista";
  if (p.ejecutora === "personal_propio") return p.workExecutor || "Personal propio";
  return p.workExecutor || "-";
};

const getInitials = (name) =>
  name ? name.split(" ").slice(0, 2).map(w => w[0]).join("").toUpperCase() : "?";

const AVATAR_COLORS = ["#648EBC","#e67e22","#27ae60","#9b59b6","#e74c3c","#16a085"];
const avatarColor = (name) => {
  if (!name) return AVATAR_COLORS[0];
  let h = 0;
  for (const c of name) h = h * 31 + c.charCodeAt(0);
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
};

// ── Componentes ──────────────────────────────────────────────────────────────
function TipoBadge({ tipo }) {
  const meta = TIPO_META[tipo];
  if (!meta) return null;
  return (
    <span
      className="wpl__tipo-badge"
      style={{ color: meta.color, background: `${meta.color}18`, borderColor: `${meta.color}40` }}
    >
      {meta.label}
    </span>
  );
}

function AprobacionDots({ status, total = 5 }) {
  const done = approvedLevels(status);
  return (
    <div className="wpl__aprobacion">
      {Array.from({ length: total }, (_, i) => (
        <span key={i} className={`wpl__aprobacion-dot${i < done ? " wpl__aprobacion-dot--done" : ""}`}>
          {i + 1}
        </span>
      ))}
    </div>
  );
}

const FilterDropdown = ({ options, value, onChange, placeholder }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const selected = options.find((o) => o.value === value);

  return (
    <div className="wpl__fd" ref={ref}>
      <button type="button" className="wpl__fd-toggle" onClick={() => setOpen((v) => !v)}>
        <span className={selected ? "" : "wpl__fd-placeholder"}>{selected ? selected.text : placeholder}</span>
        <span className="wpl__fd-icons">
          {selected && (
            <span
              className="wpl__fd-clear"
              onClick={(e) => { e.stopPropagation(); onChange(""); setOpen(false); }}
              title={placeholder}
            >
              ✕
            </span>
          )}
          <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" width="12" height="12" className={`wpl__fd-arrow${open ? " wpl__fd-arrow--open" : ""}`}>
            <path d="M6 1.5v9M2.5 7 6 10.5 9.5 7"/>
          </svg>
        </span>
      </button>
      {open && (
        <div className="wpl__fd-menu">
          {options.map((o) => (
            <button
              type="button"
              key={o.key}
              className={`wpl__fd-item${o.value === value ? " wpl__fd-item--active" : ""}`}
              onClick={() => { onChange(o.value); setOpen(false); }}
            >
              {o.text}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────────
export function ListWorkPermits({ reload, onReload, filterYear, onOpenConfig }) {
  const { accessToken, user } = useAuth();
  const { site: contextSite } = useSite();
  const { company: companyFromContext } = useCompany();
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;

  const [items,          setItems]          = useState(null);
  const [filtered,       setFiltered]       = useState([]);
  const [search,         setSearch]         = useState("");
  const [statusFilter,   setStatusFilter]   = useState("");
  const [tipoFilter,     setTipoFilter]     = useState("");
  const [paisFilter,     setPaisFilter]     = useState("");
  const [siteFilter,     setSiteFilter]     = useState("");
  const [sites,          setSites]          = useState([]);
  const [companySiteIds, setCompanySiteIds] = useState([]);
  const [companyName,    setCompanyName]    = useState("");

  const noSiteInCtx    = !contextSite       || contextSite       === "0" || contextSite       === 0;
  const noCompanyInCtx = !companyFromContext || companyFromContext === "0" || companyFromContext === 0;

  // Vista detalle
  const [showDetail,   setShowDetail]   = useState(false);
  const [detailPermit, setDetailPermit] = useState(null);

  // Formulario edición
  const [showForm,   setShowForm]   = useState(false);
  const [formPermit, setFormPermit] = useState(null);

  // Confirmar eliminar
  const [selected,    setSelected]    = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  // Observaciones
  const [showObs,       setShowObs]       = useState(false);
  const [obsItem,       setObsItem]       = useState(null);
  const [obsVal,        setObsVal]        = useState("");
  const [saving,        setSaving]        = useState(false);
  const [editingObsIdx, setEditingObsIdx] = useState(null);
  const [editingObsVal, setEditingObsVal] = useState("");

  // Plan de acción
  const [showActionPlan,    setShowActionPlan]    = useState(false);
  const [actionPlanPrefill, setActionPlanPrefill] = useState(null);

  // Archivos
  const [showAttach,   setShowAttach]   = useState(false);
  const [showFiles,    setShowFiles]    = useState(false);
  const [fileItem,     setFileItem]     = useState(null);
  const [uploading,    setUploading]    = useState(false);
  const [deletingFile, setDeletingFile] = useState(null);

  // Exportar
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [openRowMenu, setOpenRowMenu] = useState(null); // { id, top, left, openUp } | null

  useEffect(() => {
    if (!openRowMenu) return;
    const handler = (e) => { if (!e.target.closest(".wpl__kebab, .wpl__kebab-menu")) setOpenRowMenu(null); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [openRowMenu]);

  const toggleRowMenu = (id, e) => {
    if (openRowMenu?.id === id) { setOpenRowMenu(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const menuHeight = 300;
    const openUp = rect.bottom + menuHeight > window.innerHeight;
    setOpenRowMenu({
      id,
      left: rect.right - 190,
      top: openUp ? rect.top - 4 : rect.bottom + 4,
      openUp,
    });
  };
  const exportMenuRef = useRef(null);

  useEffect(() => {
    if (!showExportMenu) return;
    const onClick = (e) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target))
        setShowExportMenu(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [showExportMenu]);

  // ── Sitios ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!accessToken) return;
    const load = !noCompanyInCtx
      ? siteCtrl.getSitesByCompany(accessToken, companyFromContext, true)
      : siteCtrl.getSites(accessToken, true);
    load.then(res => setSites(Array.isArray(res) ? res : [])).catch(() => {});
  }, [accessToken, companyFromContext, noCompanyInCtx]);

  useEffect(() => { setSiteFilter(""); }, [contextSite]);

  useEffect(() => {
    if (noCompanyInCtx) { setCompanyName(""); return; }
    companyCtrl.getCompany(accessToken, companyFromContext)
      .then(res => setCompanyName(res?.name || ""))
      .catch(() => setCompanyName(""));
  }, [accessToken, companyFromContext, noCompanyInCtx]);

  useEffect(() => {
    if (noCompanyInCtx) { setCompanySiteIds([]); return; }
    siteCtrl.getSitesByCompany(accessToken, companyFromContext, true)
      .then(res => setCompanySiteIds((res || []).map(s => s._id)))
      .catch(() => setCompanySiteIds([]));
  }, [accessToken, companyFromContext, noCompanyInCtx]);

  // ── Carga ────────────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        setItems(null);
        const res = await wpController.getWorkPermits(accessToken, { active: true });
        setItems(res);
        setFiltered(res);
      } catch {
        setItems([]);
        setFiltered([]);
      }
    })();
  }, [reload, accessToken]);

  // ── Filtros ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!items) return;
    let result = [...items];
    if (filterYear) result = result.filter(p => {
      const d = p.startDateTime || p.createdAt;
      return d ? new Date(d).getFullYear() === filterYear : true;
    });
    if (statusFilter) result = result.filter(p => p.status === statusFilter);
    if (tipoFilter)   result = result.filter(p => p.tipos?.includes(tipoFilter));
    if (paisFilter)   result = result.filter(p => p.pais === paisFilter);
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(p =>
        p.customId?.toString().includes(q) ||
        fmtEjecutorDisplay(p).toLowerCase().includes(q) ||
        p.site?.name?.toLowerCase().includes(q) ||
        p.sector?.name?.toLowerCase().includes(q) ||
        p.tipos?.some(t => TIPO_META[t]?.label.toLowerCase().includes(q))
      );
    }
    if (!noSiteInCtx) {
      result = result.filter(p => (p.site?._id || p.site) === contextSite);
    } else if (!noCompanyInCtx && companySiteIds.length > 0) {
      result = result.filter(p => companySiteIds.includes(p.site?._id || p.site));
      if (siteFilter) result = result.filter(p => (p.site?._id || p.site) === siteFilter);
    } else if (siteFilter) {
      result = result.filter(p => (p.site?._id || p.site) === siteFilter);
    }
    setFiltered(result);
  }, [search, statusFilter, tipoFilter, paisFilter, filterYear, siteFilter, contextSite, noSiteInCtx, noCompanyInCtx, companySiteIds, items]);

  // ── KPI (filtrado por año) ───────────────────────────────────────────────
  const kpi = useMemo(() => {
    const base = filterYear
      ? (items || []).filter(p => {
          const d = p.startDateTime || p.createdAt;
          return d ? new Date(d).getFullYear() === filterYear : true;
        })
      : (items || []);
    return {
      activos:    base.filter(p => ["approved", "in_execution", "verified"].includes(p.status)).length,
      pendientes: base.filter(p => ["requested", "pending_n2", "pending_n3", "pending_n4"].includes(p.status)).length,
      vencidos:   base.filter(p => p.status === "expired").length,
      suspendidos:base.filter(p => p.status === "suspended").length,
    };
  }, [items, filterYear]);

  // Países disponibles en los datos actuales
  const availablePaises = useMemo(() => {
    if (!items) return [];
    const codes = [...new Set(items.map(p => p.pais).filter(Boolean))];
    return codes.map(code => ({ value: code, label: PAIS_LABELS[code] || code }));
  }, [items]);

  // ── Handlers ─────────────────────────────────────────────────────────────
  const openDetail = (p) => { setDetailPermit(p); setShowDetail(true); };
  const closeDetail = ()  => { setShowDetail(false); setDetailPermit(null); };

  const openEdit  = (p) => { setFormPermit(p); setShowForm(true); };
  const closeForm = ()  => { setShowForm(false); setFormPermit(null); };

  const openEditFromDetail = (p) => {
    closeDetail();
    setTimeout(() => openEdit(p), 50);
  };

  const handleDetailReload = async () => {
    onReload();
    if (detailPermit?._id) {
      try {
        const updated = await wpController.getWorkPermit(accessToken, detailPermit._id);
        setDetailPermit(updated);
      } catch {}
    }
  };

  const exportRows = (rows) => rows.map((p, i) => ({
    "N°":         i + 1,
    "ID":         fmtPermitNum(p),
    "Fecha":      fmtDate(p.startDateTime || p.createdAt) || "-",
    "Sitio":      p.site?.name || "-",
    "Sector":     p.sector?.name || "-",
    "País":       PAIS_LABELS[p.pais] || p.pais || "-",
    "Tipos":      (p.tipos || []).map(t => TIPO_META[t]?.label || t).join(", ") || "-",
    "Ejecutor":   fmtEjecutorDisplay(p),
    "Estado":     t(p.status) || p.status || "-",
  }));

  const handleExportXLSX = () => {
    const data = exportRows(filtered);
    if (!data.length) return;
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Permisos");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([buf], { type: "application/octet-stream" }), "permisos_de_trabajo.xlsx");
  };

  const handleExportPDF = async () => {
    if (!filtered.length) return;
    const siteName = !noSiteInCtx
      ? sites.find(s => s._id === contextSite)?.name
      : siteFilter ? sites.find(s => s._id === siteFilter)?.name : null;

    const tableColumns = [
      ...(noSiteInCtx ? [{ key: "site", header: t("site"), flex: 1, value: (r) => r.site?.name || "-" }] : []),
      { key: "id", header: "ID", flex: 0.9, value: (r) => fmtPermitNum(r) },
      { key: "date", header: t("date"), flex: 0.9, value: (r) => fmtDate(r.startDateTime || r.createdAt) || "-" },
      { key: "sector", header: t("sector"), flex: 1, value: (r) => r.sector?.name || "-" },
      { key: "pais", header: t("country"), flex: 0.8, value: (r) => PAIS_LABELS[r.pais] || r.pais || "-" },
      { key: "tipos", header: "Tipos", flex: 1.4, value: (r) => (r.tipos || []).map(tp => TIPO_META[tp]?.label || tp).join(", ") || "-" },
      { key: "ejecutor", header: "Ejecutor", flex: 1.3, value: (r) => fmtEjecutorDisplay(r) },
      {
        key: "status", header: t("status"), flex: 1, align: "center",
        render: (doc, { x, y, width, height, row }) => {
          if (!row.status) return;
          drawPill(doc, { x: x + width / 2, y: y + height / 2, text: t(row.status) || row.status, color: STATUS_META[row.status]?.color || "#888" });
        },
      },
    ];

    await buildStandardDashboardPdf({
      jsPDFCtor: jsPDF,
      pageTitle: "Listado de Permisos de Trabajo",
      companyName: [companyName, siteName].filter(Boolean).join(" · "),
      generatedOnLabel: t("generated_on"),
      table: { columns: tableColumns, rows: filtered, rowHeight: 24, headerHeight: 22, emptyMessage: t("no_results") },
      tableTitle: "Listado de Permisos de Trabajo",
      filename: "permisos_de_trabajo.pdf",
    });
  };

  const openDelete = (p) => { setSelected(p); setShowConfirm(true); };

  const handleConfirm = async () => {
    try {
      await wpController.deleteWorkPermit(accessToken, selected._id);
      toast.success("Permiso eliminado.");
      setShowConfirm(false);
      onReload();
    } catch (err) { toast.error(err?.msg || "Error."); }
  };

  const openActionPlan = (p) => {
    setActionPlanPrefill({
      workPermitId: p._id,
      moduloOrigen: "work_permit",
      findingOrigin: "plant_inspection",
    });
    setShowActionPlan(true);
  };

  const openObs  = (p) => { setObsItem(p); setObsVal(""); setShowObs(true); };
  const openAttach = (p) => { setFileItem(p); setShowAttach(true); };
  const openFiles  = (p) => { setFileItem(p); setShowFiles(true); };

  const handleSaveObs = async () => {
    if (!obsVal.trim()) return;
    setSaving(true);
    try {
      const userName = [user?.firstname, user?.lastname].filter(Boolean).join(" ") || user?.email || "Usuario";
      const updatedObs = [...(obsItem.observations || []), { text: obsVal.trim(), userName, createdAt: new Date() }];
      await wpController.updateWorkPermit(accessToken, obsItem._id, { observations: updatedObs });
      toast.success("Observación guardada.");
      setObsItem((prev) => ({ ...prev, observations: updatedObs }));
      setObsVal("");
      onReload();
    } catch { toast.error("Error al guardar."); }
    finally { setSaving(false); }
  };

  const handleDeleteObs = async (origIdx) => {
    setSaving(true);
    try {
      const updatedObs = (obsItem.observations || []).filter((_, i) => i !== origIdx);
      await wpController.updateWorkPermit(accessToken, obsItem._id, { observations: updatedObs });
      toast.success("Observación eliminada.");
      setObsItem((prev) => ({ ...prev, observations: updatedObs }));
      onReload();
    } catch { toast.error("Error al eliminar."); }
    finally { setSaving(false); }
  };

  const handleSaveObsEdit = async () => {
    if (!editingObsVal.trim()) return;
    setSaving(true);
    try {
      const updatedObs = (obsItem.observations || []).map((o, i) =>
        i === editingObsIdx ? { ...o, text: editingObsVal.trim() } : o
      );
      await wpController.updateWorkPermit(accessToken, obsItem._id, { observations: updatedObs });
      toast.success("Observación actualizada.");
      setObsItem((prev) => ({ ...prev, observations: updatedObs }));
      setEditingObsIdx(null); setEditingObsVal("");
    } catch { toast.error("Error al actualizar."); }
    finally { setSaving(false); }
  };

  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", file);
        await wpController.uploadFile(accessToken, fileItem._id, fd);
      }
      toast.success("Archivos subidos.");
      setShowAttach(false);
      onReload();
    } catch { toast.error("Error al subir archivos."); }
    finally { setUploading(false); }
  };

  const handleDeleteFile = async (fileKey) => {
    setDeletingFile(fileKey);
    try {
      await wpController.deleteFile(accessToken, fileItem._id, fileKey);
      toast.success("Archivo eliminado.");
      setFileItem(prev => ({ ...prev, files: prev.files.filter(f => f.key !== fileKey) }));
      onReload();
    } catch { toast.error("Error al eliminar."); }
    finally { setDeletingFile(null); }
  };

  const handleViewFile = async (fileKey) => {
    try {
      const url = await wpController.getFile(accessToken, fileKey);
      if (!url) { toast.error("No se pudo obtener el archivo."); return; }
      window.open(url, "_blank");
    } catch { toast.error("Error al abrir el archivo."); }
  };

  // ── Render ───────────────────────────────────────────────────────────────
  if (items === null) return <LoadingSpinner />;

  const kpiTotal     = filtered.length;
  const kpiActivos   = filtered.filter(p => ["in_execution", "approved", "verified"].includes(p.status)).length;
  const kpiPendiente = filtered.filter(p => ["draft", "requested", "pending_n2", "pending_n3", "pending_n4"].includes(p.status)).length;
  const kpiCerrados  = filtered.filter(p => ["closed", "expired", "rejected", "suspended", "paused"].includes(p.status)).length;

  return (
    <div className="wpl">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* ── Export row ── */}
      <div className="wpl__export-row">
        {onOpenConfig && (
          <button className="wpl__export-toggle" onClick={onOpenConfig}>
            <Icon name="setting" />
            {t("configure_approvals")}
          </button>
        )}
        <div className="wpl__export-dropdown" ref={exportMenuRef}>
          <button className="wpl__export-toggle" onClick={() => setShowExportMenu(v => !v)}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            {t("export_btn")}
            <svg viewBox="0 0 12 12" fill="currentColor" width="9" height="9"><path d="M6 8L2 4h8z"/></svg>
          </button>
          {showExportMenu && (
            <div className="wpl__export-menu">
              <button className="wpl__export-menu-item" onClick={() => { handleExportXLSX(); setShowExportMenu(false); }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                Excel (XLSX)
              </button>
              <button className="wpl__export-menu-item" onClick={() => { handleExportPDF(); setShowExportMenu(false); }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                PDF
              </button>
            </div>
          )}
        </div>
        {!noSiteInCtx && (
          <button className="wpl__new-btn" onClick={() => { setFormPermit(null); setShowForm(true); }} title={t("wp_new_permit")}>
            <svg viewBox="0 0 16 16" fill="currentColor" width="15" height="15">
              <path d="M8 2a1 1 0 0 1 1 1v4h4a1 1 0 1 1 0 2H9v4a1 1 0 1 1-2 0V9H3a1 1 0 1 1 0-2h4V3a1 1 0 0 1 1-1z"/>
            </svg>
          </button>
        )}
      </div>

      {/* ── KPI cards + filtros + tabla, en una sola card ── */}
      <div className="wpl__card">
        <div className="wpl__kpi-row">
          <div className="wpl__kpi-card">
            <div className="wpl__kpi-head">
              <span className="wpl__kpi-icon">
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="12" height="12"><path d="M6 10 10 6M6 6h4v4"/></svg>
              </span>
              <span className="wpl__kpi-label">{t("wp_kpi_active")}</span>
            </div>
            <span className="wpl__kpi-num">{kpi.activos}</span>
            <span className="wpl__kpi-sub">{t("wp_kpi_active_sub")}</span>
          </div>
          <div className="wpl__kpi-card">
            <div className="wpl__kpi-head">
              <span className="wpl__kpi-icon">
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="12" height="12"><path d="M6 10 10 6M6 6h4v4"/></svg>
              </span>
              <span className="wpl__kpi-label">{t("wp_kpi_pending_approval")}</span>
            </div>
            <span className="wpl__kpi-num">{kpi.pendientes}</span>
            <span className="wpl__kpi-sub">{t("wp_kpi_pending_sub")}</span>
          </div>
          <div className="wpl__kpi-card">
            <div className="wpl__kpi-head">
              <span className="wpl__kpi-icon">
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="12" height="12"><path d="M6 10 10 6M6 6h4v4"/></svg>
              </span>
              <span className="wpl__kpi-label">{t("wp_kpi_expired")}</span>
            </div>
            <span className="wpl__kpi-num">{kpi.vencidos}</span>
            <span className="wpl__kpi-sub">{t("wp_kpi_expired_sub")}</span>
          </div>
          <div className="wpl__kpi-card">
            <div className="wpl__kpi-head">
              <span className="wpl__kpi-icon">
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="12" height="12"><path d="M6 10 10 6M6 6h4v4"/></svg>
              </span>
              <span className="wpl__kpi-label">{t("wp_kpi_suspended")}</span>
            </div>
            <span className="wpl__kpi-num">{kpi.suspendidos}</span>
            <span className="wpl__kpi-sub">{t("wp_kpi_suspended_sub")}</span>
          </div>
        </div>

        <div className="wpl__filters">
          <div className="wpl__filter-block wpl__filter-search">
            <div className="wpl__search-wrap">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
              </svg>
              <input
                className="wpl__filter-input"
                placeholder="Buscar"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
          </div>
          {noSiteInCtx && sites.length > 0 && (
            <div className="wpl__filter-block">
              <FilterDropdown
                options={sites.map(s => ({ key: s._id, value: s._id, text: s.name }))}
                value={siteFilter}
                onChange={setSiteFilter}
                placeholder={t("site")}
              />
            </div>
          )}
          <div className="wpl__filter-block">
            <FilterDropdown
              options={Object.entries(TIPO_META).map(([k, v]) => ({ key: k, value: k, text: v.label }))}
              value={tipoFilter}
              onChange={setTipoFilter}
              placeholder={t("type")}
            />
          </div>
          <div className="wpl__filter-block">
            <FilterDropdown
              options={STATUS_OPTIONS.map(o => ({ key: o.key, value: o.value, text: t(o.value) }))}
              value={statusFilter}
              onChange={setStatusFilter}
              placeholder={t("status")}
            />
          </div>
          <div className="wpl__filter-block">
            <FilterDropdown
              options={availablePaises.map(p => ({ key: p.value, value: p.value, text: p.label }))}
              value={paisFilter}
              onChange={setPaisFilter}
              placeholder={t("country")}
            />
          </div>
        </div>

      {/* ── Tabla ── */}
      {filtered.length === 0 ? (
        <div className="wpl__empty">{t("no_results")}</div>
      ) : (
      <div className="wpl__table-wrap">
        <table className="wpl__table rtable-cards">
          <thead>
            <tr>
              <th>{t("wp_col_permit")}</th>
              <th>{t("type")}</th>
              <th>{t("wp_col_site_sector")}</th>
              <th>{t("executor")}</th>
              <th>{t("date")}</th>
              <th>{t("country")}</th>
              <th>{t("status")}</th>
              <th>{t("approval")}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(p => {
              const sm        = STATUS_META[p.status] || STATUS_META.draft;
              const startDate = fmtDate(p.startDateTime);
              const startTime = fmtTime(p.startDateTime);
              const endTime   = fmtTime(p.estimatedEndDateTime);
              const tipos     = p.tipos?.length ? p.tipos : [];

              return (
                <tr key={p._id} className="wpl__row" onClick={() => openDetail(p)}>

                  {/* N° Permiso */}
                  <td className="wpl__col-id" data-label={t("wp_col_permit")}>
                    <span className="wpl__id-badge">{fmtPermitNum(p)}</span>
                  </td>

                  {/* Tipos */}
                  <td className="wpl__col-tipo" data-label={t("type")}>
                    <div className="wpl__tipo-badges">
                      {tipos.length > 0
                        ? tipos.map(t => <TipoBadge key={t} tipo={t} />)
                        : <span className="wpl__no-tipo">—</span>
                      }
                    </div>
                  </td>

                  {/* Sitio · Sector */}
                  <td className="wpl__col-sitio" data-label={t("wp_col_site_sector")}>
                    <div className="wpl__sitio-name">{p.site?.name || "—"}</div>
                    {p.sector?.name && (
                      <div className="wpl__sitio-sector">{p.sector.name}</div>
                    )}
                  </td>

                  {/* Ejecutor */}
                  <td className="wpl__col-ejecutor" data-label={t("executor")}>{fmtEjecutorDisplay(p)}</td>

                  {/* Fecha */}
                  <td className="wpl__col-fecha" data-label={t("date")}>
                    {startDate && <div>{startDate}</div>}
                    {(startTime || endTime) && (
                      <div className="wpl__fecha-rango">
                        {startTime}{endTime ? ` – ${endTime}` : ""}
                      </div>
                    )}
                    {!startDate && <span className="wpl__no-fecha">—</span>}
                  </td>

                  {/* País */}
                  <td className="wpl__col-pais" data-label={t("country")}>
                    {p.pais
                      ? <span className="wpl__pais-pill">{PAIS_LABELS[p.pais] || p.pais}</span>
                      : <span className="wpl__no-tipo">—</span>
                    }
                  </td>

                  {/* Estado */}
                  <td className="wpl__col-estado" data-label={t("status")}>
                    <span
                      className="wpl__estado-badge"
                      style={{ color: sm.color, background: sm.bg }}
                    >
                      {sm.dot && <span className="wpl__estado-dot" />}
                      {t(p.status)}
                    </span>
                  </td>

                  {/* Aprobación */}
                  <td className="wpl__col-aprobacion" data-label={t("approval")}>
                    <AprobacionDots status={p.status} />
                  </td>

                  {/* Acciones */}
                  <td className="wpl__col-actions" data-label="" onClick={e => e.stopPropagation()}>
                    <div className="wpl__kebab">
                      <button
                        className="wpl__kebab-btn"
                        title={t("actions")}
                        onClick={(e) => toggleRowMenu(p._id, e)}
                      >
                        <Icon name="ellipsis vertical" />
                      </button>
                    </div>
                    {openRowMenu?.id === p._id && ReactDOM.createPortal(
                      <div
                        className="wpl__kebab-menu"
                        style={{
                          position: "fixed",
                          top: openRowMenu.top,
                          left: openRowMenu.left,
                          transform: openRowMenu.openUp ? "translateY(-100%)" : "none",
                        }}
                      >
                        <button onClick={() => { openEdit(p); setOpenRowMenu(null); }}>
                          <Icon name="pencil" />{t("edit")}
                        </button>
                        <button onClick={() => { openActionPlan(p); setOpenRowMenu(null); }}>
                          <Icon name="exclamation triangle" />{t("action_plan")}
                        </button>
                        <button onClick={() => { openObs(p); setOpenRowMenu(null); }}>
                          <Icon name="comment outline" />{t("observations")}
                        </button>
                        <button onClick={() => { openAttach(p); setOpenRowMenu(null); }}>
                          <Icon name="attach" />{t("attach_files")}
                        </button>
                        <button onClick={() => { openFiles(p); setOpenRowMenu(null); }}>
                          <Icon name="folder open outline" />{t("view_files")}
                        </button>
                        <button className="wpl__kebab-menu-item--danger" onClick={() => { openDelete(p); setOpenRowMenu(null); }}>
                          <Icon name="trash alternate outline" />{t("delete")}
                        </button>
                      </div>,
                      document.body
                    )}
                  </td>

                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      )}
      </div>

      {/* ── Drawer detalle ── */}
      <BasicDrawer show={showDetail} close={closeDetail} width="80vw">
        {showDetail && (
          <WorkPermitDetail
            permit={detailPermit}
            onEdit={openEditFromDetail}
            onClose={closeDetail}
            onReload={handleDetailReload}
          />
        )}
      </BasicDrawer>

      {/* ── Drawer formulario ── */}
      <BasicDrawer show={showForm} close={closeForm} width="80vw" hideHeader flush>
        {showForm && <WorkPermitForm permit={formPermit} close={closeForm} onReload={handleDetailReload} />}
      </BasicDrawer>

      {/* ── Plan de acción vinculado ── */}
      <BasicDrawer show={showActionPlan} close={() => { setShowActionPlan(false); setActionPlanPrefill(null); }} width="70vw" hideHeader flush>
        <ActionPlanForm
          show={showActionPlan}
          close={() => { setShowActionPlan(false); setActionPlanPrefill(null); }}
          prefill={actionPlanPrefill}
          onSaved={() => { setShowActionPlan(false); setActionPlanPrefill(null); }}
        />
      </BasicDrawer>

      {/* ── Confirmar eliminar ── */}
      <ConfirmDialog
        open={showConfirm}
        title={t("delete")}
        message={`${t("confirm_delete_permit")} ${fmtPermitNum(selected || {})}?`}
        onCancel={() => setShowConfirm(false)}
        onConfirm={handleConfirm}
        confirmLabel={t("accept")}
        cancelLabel={t("cancel")}
        confirmDanger
      />

      {/* ── Observaciones ── */}
      {showObs && (
        <div className="wpl-obs-backdrop" onClick={e => { if (e.target === e.currentTarget) { setShowObs(false); setEditingObsIdx(null); setEditingObsVal(""); } }}>
          <div className="wpl-obs-modal">
            <div className="wpl-obs-header">
              <h3 className="wpl-obs-title">{t("observations")}</h3>
              <button className="wpl-obs-close" type="button" onClick={() => { setShowObs(false); setEditingObsIdx(null); setEditingObsVal(""); }}>
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="wpl-obs-body">
              {obsItem?.observations?.length > 0 && (
                <div className="wpl__obs-history">
                  {[...obsItem.observations]
                    .map((obs, origIdx) => ({ obs, origIdx }))
                    .reverse()
                    .map(({ obs, origIdx }) => {
                      const currentUser = [user?.firstname, user?.lastname].filter(Boolean).join(" ") || user?.email || "Usuario";
                      const isOwner   = obs.userName === currentUser;
                      const isEditing = editingObsIdx === origIdx;
                      return (
                        <div key={origIdx} className="wpl__obs-entry">
                          <span className="wpl__obs-avatar" style={{ background: avatarColor(obs.userName) }}>
                            {getInitials(obs.userName)}
                          </span>
                          <div className="wpl__obs-body">
                            <div className="wpl__obs-meta">
                              <span className="wpl__obs-user">{obs.userName}</span>
                              <span className="wpl__obs-date">
                                {obs.createdAt ? new Date(obs.createdAt).toLocaleDateString("es-AR") : ""}
                              </span>
                              {isOwner && !isEditing && (
                                <span className="wpl__obs-actions">
                                  <Icon name="pencil" link onClick={() => { setEditingObsIdx(origIdx); setEditingObsVal(obs.text); }} />
                                  <Icon name="trash alternate" link color="red" onClick={() => handleDeleteObs(origIdx)} />
                                </span>
                              )}
                            </div>
                            {isEditing ? (
                              <div>
                                <textarea
                                  rows={2}
                                  value={editingObsVal}
                                  onChange={e => setEditingObsVal(e.target.value)}
                                  style={{ width: "100%", marginBottom: "0.5rem" }}
                                />
                                <div style={{ display: "flex", gap: "0.4rem" }}>
                                  <button type="button" disabled={saving || !editingObsVal.trim()} onClick={handleSaveObsEdit} className="obs-icon-btn obs-icon-btn--confirm">
                                    <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><path d="M2 6.5L5 9.5L11 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                  </button>
                                  <button type="button" onClick={() => { setEditingObsIdx(null); setEditingObsVal(""); }} className="obs-icon-btn obs-icon-btn--cancel">
                                    <svg width="11" height="11" viewBox="0 0 11 11" fill="none"><path d="M8.5 2.5L2.5 8.5M2.5 2.5L8.5 8.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="wpl__obs-text">{obs.text}</div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
              <textarea
                rows={3}
                value={obsVal}
                onChange={e => setObsVal(e.target.value)}
                placeholder={t("enter_observations")}
                style={{ width: "100%", marginTop: "0.75rem" }}
              />
              <div className="wpl-obs-actions">
                <button className="wpl-obs-btn-confirm" disabled={saving || !obsVal.trim()} onClick={handleSaveObs}>
                  {saving ? "…" : t("save")}
                </button>
                <button className="wpl-obs-btn-cancel" onClick={() => { setShowObs(false); setEditingObsIdx(null); setEditingObsVal(""); }}>{t("cancel")}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Adjuntar archivos ── */}
      {showAttach && ReactDOM.createPortal(
        <div className="wpl-obs-backdrop" onClick={(e) => { if (e.target === e.currentTarget) setShowAttach(false); }}>
          <div className="wpl-obs-modal">
            <div className="wpl-obs-header">
              <p className="wpl-obs-title">{t("attach_files")}</p>
              <button className="wpl-obs-close" onClick={() => setShowAttach(false)}>
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M13.5 4.5L4.5 13.5M4.5 4.5L13.5 13.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
              </button>
            </div>
            <div className="wpl-obs-body">
              <FileDropzone
                onUpload={handleUpload}
                uploading={uploading}
                onCancel={() => setShowAttach(false)}
                supportedLabel={t("ap_supported_files")}
              />
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ── Ver archivos ── */}
      {showFiles && ReactDOM.createPortal(
        <div className="wpl-obs-backdrop" onClick={(e) => { if (e.target === e.currentTarget) setShowFiles(false); }}>
          <div className="wpl-obs-modal">
            <div className="wpl-obs-header">
              <p className="wpl-obs-title">{t("attached_files")}</p>
              <button className="wpl-obs-close" onClick={() => setShowFiles(false)}>
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M13.5 4.5L4.5 13.5M4.5 4.5L13.5 13.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
              </button>
            </div>
            <div className="wpl-obs-body">
              {!fileItem?.files?.length ? (
                <p style={{ color: "#999", textAlign: "center", padding: "1.5rem 0", margin: 0 }}>{t("no_attached_files")}</p>
              ) : (
                <ul style={{ listStyle: "none", padding: 0, margin: "0 0 20px" }}>
                  {fileItem.files.map((f, i) => (
                    <li key={i} style={{ display: "flex", alignItems: "center", gap: "0.75rem", padding: "10px 12px", borderRadius: "10px", background: "#fff", border: "1px solid #E5E7EB", marginBottom: "8px" }}>
                      <Icon name={/\.(jpg|jpeg|png|gif|webp)$/i.test(f.name) ? "image outline" : "file outline"} style={{ color: "#4A7FA5", margin: 0, fontSize: "1.1rem" }} />
                      <span style={{ flex: 1, fontSize: "0.875rem", color: "#374151", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{f.name}</span>
                      <button title={t("view")} onClick={() => handleViewFile(f.key)}
                        style={{ border: "none", background: "#4A7FA5", color: "#fff", borderRadius: "50%", width: "30px", height: "30px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <Icon name="eye" style={{ margin: 0, fontSize: "0.8rem" }} />
                      </button>
                      <EvidenceTextButton module="workPermit" fileKey={f.key} fileName={f.name} />
                      <button title={t("delete")} onClick={() => handleDeleteFile(f.key)} disabled={deletingFile !== null}
                        style={{ border: "none", background: deletingFile === f.key ? "#ccc" : "#E74C3C", color: "#fff", borderRadius: "50%", width: "30px", height: "30px", cursor: deletingFile !== null ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <Icon name="trash" style={{ margin: 0, fontSize: "0.8rem" }} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <div style={{ display: "flex", justifyContent: "center", marginTop: "1.5rem" }}>
                <button onClick={() => setShowFiles(false)}
                  style={{ border: "none", borderRadius: "20px", padding: "10px 30px", fontSize: "14px", fontWeight: 600, fontFamily: "'Archivo', sans-serif", cursor: "pointer", background: "#9BAFC4", color: "#fff" }}>
                  {t("close")}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
